import React, { useEffect, useState } from 'react';
import {
  RiArrowLeftLine as BackIcon,
  RiFileTextLine as DocumentIcon,
  RiFileList2Line as ExhibitListIcon,
  RiAddLine as AddIcon,
  RiSparklingLine as SparklesIcon,
  RiFileWordLine,
  RiGoogleLine,
} from 'react-icons/ri';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';
import PetitionService, { Petition } from '@/api/petitions';
import { ApiService } from '@/api/cases';
import PetitionEditor from '@/components/petitions/editor/PetitionEditor';
import ExhibitListViewer from './ExhibitListViewer';
import PetitionGenerationForm from '@/components/petitions/PetitionGenerationForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LettersApi } from '@/api';
import SourcesSidebar from '@/components/letters/editor/SourcesSidebar';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface DraftingTabProps {
  caseId: string;
}

interface ExhibitList {
  id: string;
  title: string;
  createdAt: string;
  totalExhibits: number;
  visaType: string;
}

const DraftingTab: React.FC<DraftingTabProps> = ({ caseId }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showGenerationForm, setShowGenerationForm] = useState(false);
  const [petitions, setPetitions] = useState<Petition[]>([]);
  const [exhibitLists, setExhibitLists] = useState<ExhibitList[]>([]);
  const [selectedPetition, setSelectedPetition] = useState<Petition | null>(
    null
  );
  const [selectedExhibitList, setSelectedExhibitList] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [caseData, setCaseData] = useState<any>(null);
  const [isLoadingSources, setIsLoadingSources] = useState<boolean>(false);
  const [sources, setSources] = useState<
    Record<string, { text: string; score: number; queryText: string }[]>
  >({});
  const [showSources, setShowSources] = useState<boolean>(false);

  // Get petition ID from URL params for persistence
  const petitionIdFromUrl = searchParams.get('petitionId');
  const exhibitListIdFromUrl = searchParams.get('exhibitListId');

  useEffect(() => {
    loadCaseData();
    loadDraftingItems();
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      const response = await ApiService.getCase(caseId);
      // API returns { success: true, case: {...} }, not { success: true, data: {...} }
      // Use same fallback pattern as pages/cases/[id].tsx
      const caseInfo = (response as any).case || response.data || response;
      if (response.success && caseInfo) {
        setCaseData(caseInfo);
      }
    } catch (err: any) {
      console.error('Failed to load case data:', err);
    }
  };

  const loadDraftingItems = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load petitions
      const petitionsResponse =
        await PetitionService.getPetitionsForCase(caseId);
      if (petitionsResponse.success && petitionsResponse.data) {
        // Show all petitions that are ready for editing (exclude only generating/in_progress/failed)
        const editablePetitions = (
          Array.isArray(petitionsResponse.data)
            ? petitionsResponse.data
            : [petitionsResponse.data]
        ).filter(
          p =>
            p.status !== 'generating' &&
            p.status !== 'in_progress' &&
            p.status !== 'failed'
        );
        setPetitions(editablePetitions);
      } else {
        setPetitions([]);
      }

      // Load exhibit lists
      const exhibitListsResponse = await ApiService.getExhibitLists(caseId);
      if (exhibitListsResponse.success && exhibitListsResponse.data) {
        const lists = Array.isArray(exhibitListsResponse.data)
          ? exhibitListsResponse.data
          : [exhibitListsResponse.data];
        setExhibitLists(lists);
      } else {
        setExhibitLists([]);
      }
    } catch (err: any) {
      console.error('Failed to load drafting items:', err);
      setError(err.message || 'Failed to load drafting items');
      setPetitions([]);
      setExhibitLists([]);
    } finally {
      setLoading(false);
    }
  };

  // Restore selection from URL params after petitions are loaded
  useEffect(() => {
    if (petitions.length > 0 && petitionIdFromUrl && !selectedPetition) {
      // Check if the petition exists in our loaded list
      const petition = petitions.find(p => p.id === petitionIdFromUrl);
      if (petition) {
        handleSelectPetition(petitionIdFromUrl);
      }
    }
  }, [petitions, petitionIdFromUrl]);

  // Restore exhibit list selection from URL params
  useEffect(() => {
    if (exhibitLists.length > 0 && exhibitListIdFromUrl && !selectedExhibitList) {
      const exhibitList = exhibitLists.find(e => e.id === exhibitListIdFromUrl);
      if (exhibitList) {
        handleSelectExhibitList(exhibitListIdFromUrl);
      }
    }
  }, [exhibitLists, exhibitListIdFromUrl]);

  const handleCheckSources = async (query: string) => {
    console.log('DraftingTab handleCheckSources called', {
      query,
      caseId,
    });

    analytics.track(ANALYTICS_EVENTS.PETITION_TEXT_CHECK_SOURCES_CLICKED, {
      case_id: caseId,
      query_length: query.length,
    });

    if (!caseId) {
      console.error('Missing caseId for sources search');
      alert('Cannot check sources: Case ID is missing.');
      return;
    }

    const trimmedQuery = (query || '').trim();
    if (!trimmedQuery || trimmedQuery.length < 3) {
      console.warn('Query too short:', trimmedQuery);
      return;
    }

    try {
      setSources({});
      setIsLoadingSources(true);
      console.log('Searching documents with:', { caseId, query: trimmedQuery });

      const res = await LettersApi.searchInDocuments({
        case_id: caseId,
        query: trimmedQuery,
        search_type: 'fuzzy',
        max_results: 8,
      });

      console.log('Search results:', res);

      const grouped: Record<
        string,
        { text: string; score: number; queryText: string }[]
      > = {};
      (res?.results || []).forEach(r => {
        const file = r.fileName || 'Document';
        if (!grouped[file]) grouped[file] = [];
        grouped[file].push({
          text: r.content,
          score: r.relevance,
          queryText: res.query || trimmedQuery,
        });
      });
      setSources(grouped);
      setShowSources(true);
    } catch (e) {
      console.error('Sources search failed', e);
      alert('Failed to search sources. Check the console for details.');
    } finally {
      setIsLoadingSources(false);
    }
  };

  const handleCloseSources = () => {
    setShowSources(false);
    setSources({});
  };

  const handleExportToWord = async (petitionId: string) => {
    try {
      analytics.track(ANALYTICS_EVENTS.PACKAGE_DOWNLOAD_WORD_CLICKED, {
        case_id: caseId,
        petition_id: petitionId,
      });
      const blob = await PetitionService.downloadToWord(petitionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petition-${petitionId.substring(0, 8)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export to Word failed:', error);
      alert('Failed to export petition to Word');
    }
  };

  const handleExportToGoogleDocs = async (petitionId: string) => {
    try {
      const blob = await PetitionService.downloadForGoogleDocs(petitionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petition-${petitionId.substring(0, 8)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export to Google Docs failed:', error);
      alert('Failed to export petition for Google Docs');
    }
  };

  const handleSelectPetition = async (petitionId: string) => {
    try {
      const response = await PetitionService.getPetition(petitionId);
      if (response.success && response.data) {
        setSelectedPetition(response.data);
        setSelectedExhibitList(null); // Clear exhibit list selection
        // Update URL to persist selection
        setSearchParams({ petitionId });
      }
    } catch (err: any) {
      console.error('Failed to load petition:', err);
      setError(err.message || 'Failed to load petition details');
    }
  };

  const handleSelectExhibitList = async (exhibitListId: string) => {
    try {
      const response = await ApiService.getExhibitList(caseId, exhibitListId);
      if (response.success && response.data) {
        setSelectedExhibitList(response.data);
        setSelectedPetition(null); // Clear petition selection
        // Update URL to persist selection
        setSearchParams({ exhibitListId });
      }
    } catch (err: any) {
      console.error('Failed to load exhibit list:', err);
      setError(err.message || 'Failed to load exhibit list details');
    }
  };

  const handlePetitionGenerationSuccess = async (petitionId: string) => {
    setSuccess('Petition generated successfully!');
    setShowGenerationForm(false);

    // Reload items to show the new petition in the list
    await loadDraftingItems();

    // Stay on the list view instead of automatically opening the petition
    // User can click "Edit & Review" when they're ready

    setTimeout(() => {
      setSuccess(null);
    }, 3000);
  };

  const handlePetitionGenerationError = (errorMessage: string) => {
    setError(errorMessage);
    setShowGenerationForm(false);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      return date.toLocaleString();
    } catch {
      return 'Date not available';
    }
  };

  // Editing mode - Petition
  if (selectedPetition) {
    return (
      <div className="space-y-4">
        {/* Header with Back button and Export buttons */}
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedPetition(null);
              setSearchParams({}); // Clear URL params
              loadDraftingItems(); // Refresh list to show newly created petitions
            }}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back to Drafting
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportToWord(selectedPetition.id)}
              className="inline-flex items-center gap-1"
            >
              <RiFileWordLine className="h-4 w-4" /> Edit in Word
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportToGoogleDocs(selectedPetition.id)}
              className="inline-flex items-center gap-1"
            >
              <RiGoogleLine className="h-4 w-4" /> Edit in Google Docs
            </Button>
          </div>
        </div>

        {/* Editor with Sidebar */}
        <div className="w-full flex">
          <div className="flex-1 min-w-0">
            <PetitionEditor
              petitionId={selectedPetition.id}
              caseId={caseId}
              initialContent={selectedPetition.content?.sections || {}}
              onCheckSources={handleCheckSources}
            />
          </div>
          {showSources && (
            <div className="hidden lg:block ml-4 w-80">
              <SourcesSidebar
                sources={sources}
                isLoading={isLoadingSources}
                caseId={caseId}
                onClose={handleCloseSources}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Viewing mode - Exhibit List (read-only for now, as requested)
  if (selectedExhibitList) {
    return (
      <div className="space-y-4">
        {/* ExhibitListViewer has its own back button, no need for duplicate */}
        <ExhibitListViewer
          exhibitList={selectedExhibitList}
          caseId={caseId}
          onBack={() => {
            setSelectedExhibitList(null);
            setSearchParams({}); // Clear URL params
          }}
        />
      </div>
    );
  }

  // List mode
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (
    petitions.length === 0 &&
    exhibitLists.length === 0 &&
    !showGenerationForm
  ) {
    return (
      <div className="space-y-6">
        {error && (
          <Card padding="sm">
            <div className="text-sm text-error-600">{error}</div>
          </Card>
        )}
        {success && (
          <Card padding="sm">
            <div className="text-sm text-success-600">{success}</div>
          </Card>
        )}

        <Card padding="lg">
          <div className="text-center py-12">
            <div className="flex flex-col items-center space-y-6">
              <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center">
                <DocumentIcon className="h-8 w-8 text-neutral-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-neutral-900">
                  No Items Ready for Drafting
                </h3>
                <p className="text-neutral-500 text-sm max-w-md mx-auto">
                  Generate a petition or exhibit list to get started with
                  drafting and editing.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowGenerationForm(true);
                    analytics.track(
                      ANALYTICS_EVENTS.DRAFTING_GENERATE_PETITION_CLICKED,
                      {
                        case_id: caseId,
                      }
                    );
                  }}
                  className="inline-flex items-center gap-2"
                >
                  <SparklesIcon className="h-4 w-4" />
                  Generate Petition
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/cases/${caseId}/exhibit-lists`)}
                  className="inline-flex items-center gap-2"
                >
                  <ExhibitListIcon className="h-4 w-4" />
                  Generate Exhibit List
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Show generation form
  if (showGenerationForm) {
    return (
      <div className="space-y-6">
        {error && (
          <Card padding="sm">
            <div className="text-sm text-error-600">{error}</div>
          </Card>
        )}
        {success && (
          <Card padding="sm">
            <div className="text-sm text-success-600">{success}</div>
          </Card>
        )}

        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGenerationForm(false)}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back
          </Button>
          <h3 className="text-lg font-semibold text-neutral-900">
            Generate New Petition
          </h3>
        </div>

        <Card padding="md">
          <PetitionGenerationForm
            caseId={caseId}
            visaType={caseData?.visaType}
            onCancel={() => setShowGenerationForm(false)}
            onSuccess={handlePetitionGenerationSuccess}
            onError={handlePetitionGenerationError}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card padding="sm">
          <div className="text-sm text-error-600">{error}</div>
        </Card>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-neutral-900">
            Drafting & Review ({petitions.length + exhibitLists.length})
          </h2>
          <p className="text-sm text-neutral-600">
            Edit petition sections, review exhibit lists, approve/reject
            content, and regenerate with AI.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShowGenerationForm(true);
              analytics.track(
                ANALYTICS_EVENTS.DRAFTING_GENERATE_PETITION_CLICKED,
                {
                  case_id: caseId,
                }
              );
            }}
            className="inline-flex items-center gap-2"
          >
            <SparklesIcon className="h-4 w-4" />
            New Petition
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/cases/${caseId}/exhibit-lists`)}
            className="inline-flex items-center gap-2"
          >
            <ExhibitListIcon className="h-4 w-4" />
            New Exhibit List
          </Button>
        </div>
      </div>

      {/* Petitions section */}
      {petitions.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-neutral-700 uppercase tracking-wide">
            Petitions ({petitions.length})
          </h3>
          <div className="space-y-4">
            {petitions.map(petition => (
              <Card
                key={petition.id}
                padding="md"
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleSelectPetition(petition.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <DocumentIcon className="h-5 w-5 text-primary-600" />
                      <h3 className="text-base font-semibold text-neutral-900">
                        {petition.petitionType || 'Immigration Petition'}
                      </h3>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                        Ready to Edit
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 space-y-1">
                      <p>Generated: {formatDate(petition.createdAt)}</p>
                      {petition.metadata?.beneficiaryName && (
                        <p>Beneficiary: {petition.metadata.beneficiaryName}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      handleSelectPetition(petition.id);
                      analytics.track(
                        ANALYTICS_EVENTS.DRAFTING_EDIT_REVIEW_CLICKED,
                        {
                          case_id: caseId,
                          petition_id: petition.id,
                        }
                      );
                    }}
                  >
                    Edit & Review
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Exhibit Lists section */}
      {exhibitLists.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-neutral-700 uppercase tracking-wide">
            Exhibit Lists ({exhibitLists.length})
          </h3>
          <div className="space-y-4">
            {exhibitLists.map(exhibitList => (
              <Card
                key={exhibitList.id}
                padding="md"
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleSelectExhibitList(exhibitList.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <ExhibitListIcon className="h-5 w-5 text-blue-600" />
                      <h3 className="text-base font-semibold text-neutral-900">
                        {exhibitList.title || 'Exhibit List'}
                      </h3>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {exhibitList.totalExhibits} exhibits
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 space-y-1">
                      <p>Created: {formatDate(exhibitList.createdAt)}</p>
                      <p>Visa Type: {exhibitList.visaType}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSelectExhibitList(exhibitList.id)}
                  >
                    Review
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftingTab;
